import './App.css';
import { useState, useEffect } from 'react';
import Icon from '@mdi/react';
import { mdiRefresh, mdiCog, mdiSortAscending, mdiSortDescending, mdiPrinterOutline, mdiArrowLeftThick, mdiArrowRightThick, mdiPlus } from '@mdi/js';

// Order Label Component - Shows all items
function OrderLabel({ order, storeName }) {
  if (!order) return null;

  return (
    <div className="order-label-view">
      <div className="label-order-number">ORDER #{order.order_number}</div>
      <div className="label-store-name">{storeName}</div>
      <div className="label-customer-section">
        <div className="label-customer-name">
          {order.customer.firstName} {order.customer.lastName}
        </div>
      </div>
      <div className="label-pickup-info">
        <div>{order.pickup_date}</div>
        <div>READY BY: {order.pickup_time}</div>
      </div>
      <div className="label-items-section">
        <div className="label-items-title">ORDER ITEMS</div>
        {order.order_data.items.map((item, index) => (
          <div key={index} className="label-item">
            <span className="label-item-name">{item.title}</span>
            <span className="label-item-quantity"> x {item.quantity}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Order Details Label Component - Shows specific item
function OrderDetailsLabel({ item, storeName }) {
  if (!item) return null;

  return (
    <div className="order-label-view">
      <div className="label-order-number">ORDER #{item.orderNumber}</div>
      <div className="label-store-name">{storeName}</div>
      <div className="label-customer-section">
        <div className="label-customer-name">
          {item.customer.firstName} {item.customer.lastName}
        </div>
      </div>
      <div className="label-pickup-info">
        <div>{item.pickupDate}</div>
        <div>READY BY: {item.pickupTime}</div>
      </div>
      <div className="label-items-section">
        <div className="label-items-title">ITEM DETAIL</div>
        <div className="label-single-item">
          <div className="label-item-name">{item.title}</div>
          <div className="label-item-quantity">Quantity: {item.quantity}</div>
        </div>
      </div>
    </div>
  );
}

function App() {
  const [serverConfigured, setServerConfigured] = useState(null); // null = checking, true = configured, false = not configured
  const [selectedTemplate, setSelectedTemplate] = useState('order');
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [settings, setSettings] = useState({
    server: '',
    printerIp: '',
    printerPort: '',
    printerWidth: '576'
  });
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState('All');
  const [selectedStore, setSelectedStore] = useState('All');
  const [selectAll, setSelectAll] = useState(false);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [displayOrder, setDisplayOrder] = useState(null);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState(null); // null, 'asc', 'desc'
  const [originalOrders, setOriginalOrders] = useState([]);
  const [currentDetailItemIndex, setCurrentDetailItemIndex] = useState(0);
  const [printedOrders, setPrintedOrders] = useState(new Set());
  const [showManualOrderModal, setShowManualOrderModal] = useState(false);
  const [manualOrder, setManualOrder] = useState({
    orderNumber: '',
    storeId: '1',
    customerFirstName: '',
    customerLastName: '',
    pickupDate: '',
    pickupTime: '',
    total: ''
  });
  const [manualOrderItems, setManualOrderItems] = useState([
    { title: '', quantity: 1, unit_price: 0 }
  ]);

  // Date Events State
  const [showDateEventsModal, setShowDateEventsModal] = useState(false);
  const [showDateEditModal, setShowDateEditModal] = useState(false);
  const [dateRanges, setDateRanges] = useState([]);
  const [editingDateRange, setEditingDateRange] = useState(null);
  const [dateRangeForm, setDateRangeForm] = useState({
    name: '',
    start: '',
    end: ''
  });

  // Store mapping
  const storeMap = {
    1: 'Princeton',
    2: 'Fort Lee',
    3: 'Palisades Park'
  };

  // Get the first selected order for display
  const getDisplayOrder = () => {
    if (selectedOrders.length === 0) return null;
    return orders.find(order => order.id === selectedOrders[0]);
  };

  // Get all items from all selected orders as a flat array
  const getAllSelectedItems = () => {
    if (selectedOrders.length === 0) return [];

    const selectedOrderObjects = orders.filter(order => selectedOrders.includes(order.id));
    const allItems = [];

    selectedOrderObjects.forEach(order => {
      order.order_data.items.forEach(item => {
        allItems.push({
          ...item,
          orderNumber: order.order_number,
          orderId: order.id,
          storeId: order.store_id,
          customer: order.customer,
          pickupDate: order.pickup_date,
          pickupTime: order.pickup_time
        });
      });
    });

    return allItems;
  };

  // Get the current item being viewed in details
  const getCurrentDetailItem = () => {
    const allItems = getAllSelectedItems();
    if (allItems.length === 0) return null;
    return allItems[currentDetailItemIndex] || allItems[0];
  };

  // Check if print server is configured
  const checkPrintServer = async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000); // 3 second timeout

      const response = await fetch('http://localhost:1120/', {
        method: 'GET',
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        const data = await response.json();
        if (data.status === 'ok') {
          console.log('Print server configured:', data.message);
          setServerConfigured(true);
          return;
        }
      }

      setServerConfigured(false);
    } catch (err) {
      console.error('Print server check failed:', err);
      setServerConfigured(false);
    }
  };

  // Check print server on component mount
  useEffect(() => {
    checkPrintServer();
  }, []);

  // Load settings from cookies on component mount
  useEffect(() => {
    const loadedSettings = {
      server: getCookie('server') || '',
      printerIp: getCookie('printerIp') || '',
      printerPort: getCookie('printerPort') || '',
      printerWidth: getCookie('printerWidth') || '576'
    };
    setSettings(loadedSettings);

    // Load printed orders from localStorage
    const savedPrintedOrders = localStorage.getItem('printedOrders');
    if (savedPrintedOrders) {
      try {
        const parsedOrders = JSON.parse(savedPrintedOrders);
        setPrintedOrders(new Set(parsedOrders));
      } catch (err) {
        console.error('Error loading printed orders:', err);
      }
    }
  }, []);

  // Fetch orders and date ranges on component mount
  useEffect(() => {
    if (settings.server) {
      fetchOrders();
      fetchDateRanges();
    }
  }, [settings.server]);

  // Cookie helper functions
  const setCookie = (name, value, days = 365) => {
    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
  };

  const getCookie = (name) => {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  };

  // Fetch orders from API
  const fetchOrders = async () => {
    if (!settings.server) {
      setError('Please configure server in settings');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Ensure server URL has protocol
      let serverUrl = settings.server;
      if (!serverUrl.startsWith('http://') && !serverUrl.startsWith('https://')) {
        serverUrl = 'https://' + serverUrl;
      }

      const apiUrl = `${serverUrl}/api/?request=getOrders`;
      console.log('Fetching from:', apiUrl);

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        mode: 'cors',
      });

      console.log('Response status:', response.status);
      console.log('Response URL:', response.url);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.status === 'success') {
        const fetchedOrders = result.data || [];
        setOrders(fetchedOrders);
        setOriginalOrders([...fetchedOrders]); // Save original order
      } else {
        throw new Error(result.message || 'Failed to fetch orders');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error fetching orders:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    console.log('Refresh clicked');
    fetchOrders();
  };

  const handleSettings = () => {
    setShowSettingsModal(true);
  };

  const handleCloseModal = () => {
    setShowSettingsModal(false);
  };

  const handleSettingChange = (e) => {
    const { name, value } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveSettings = () => {
    // Save to cookies
    setCookie('server', settings.server);
    setCookie('printerIp', settings.printerIp);
    setCookie('printerPort', settings.printerPort);
    setCookie('printerWidth', settings.printerWidth);

    console.log('Settings saved:', settings);
    setShowSettingsModal(false);
  };

  // Mark order as printed and save to localStorage
  const markOrderAsPrinted = (orderNumber) => {
    setPrintedOrders(prev => {
      const newSet = new Set(prev);
      newSet.add(orderNumber);
      // Save to localStorage
      localStorage.setItem('printedOrders', JSON.stringify([...newSet]));
      return newSet;
    });
  };

  // Filter orders based on selected date and store
  const getFilteredOrders = () => {
    let filtered = orders.filter(order => {
      let dateMatch = selectedDate === 'All';

      if (!dateMatch) {
        // Find the selected date range
        const selectedRange = dateRanges.find(range => String(range.id) === selectedDate);
        if (selectedRange) {
          // Check if pickup_date is within the date range
          const pickupDate = order.pickup_date;
          dateMatch = pickupDate >= selectedRange.start && pickupDate <= selectedRange.end;
        }
      }

      const storeMatch = selectedStore === 'All' || storeMap[order.store_id] === selectedStore;
      return dateMatch && storeMatch;
    });

    // Apply sorting if a column is selected
    if (sortColumn && sortDirection) {
      filtered = [...filtered].sort((a, b) => {
        let aValue, bValue;

        switch (sortColumn) {
          case 'order_number':
            aValue = a.order_number;
            bValue = b.order_number;
            break;
          case 'store':
            aValue = storeMap[a.store_id];
            bValue = storeMap[b.store_id];
            break;
          case 'customer':
            aValue = `${a.customer.firstName} ${a.customer.lastName}`;
            bValue = `${b.customer.firstName} ${b.customer.lastName}`;
            break;
          case 'total':
            aValue = a.total;
            bValue = b.total;
            break;
          case 'pickup_date':
            aValue = a.pickup_date;
            bValue = b.pickup_date;
            break;
          case 'pickup_time':
            aValue = a.pickup_time;
            bValue = b.pickup_time;
            break;
          case 'status':
            aValue = printedOrders.has(a.order_number) ? 1 : 0;
            bValue = printedOrders.has(b.order_number) ? 1 : 0;
            break;
          default:
            return 0;
        }

        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  };

  // Handle date filter
  const handleDateFilter = (date) => {
    setSelectedDate(date);
  };

  // Handle store filter
  const handleStoreFilter = (store) => {
    setSelectedStore(store);
  };

  // Handle select all checkbox
  const handleSelectAll = () => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);

    if (newSelectAll) {
      // Select all filtered orders
      const filteredOrders = getFilteredOrders();
      setSelectedOrders(filteredOrders.map(order => order.id));
    } else {
      // Deselect all
      setSelectedOrders([]);
    }
  };

  // Handle individual order checkbox
  const handleOrderSelect = (orderId) => {
    setSelectedOrders(prev => {
      if (prev.includes(orderId)) {
        return prev.filter(id => id !== orderId);
      } else {
        return [...prev, orderId];
      }
    });
  };

  // Handle row click to select order
  const handleRowClick = (orderId, e) => {
    // Don't trigger if clicking on the checkbox itself
    if (e.target.type === 'checkbox') return;
    handleOrderSelect(orderId);
  };

  // Handle column sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      // Cycle through: desc → asc → null
      if (sortDirection === 'desc') {
        setSortDirection('asc');
      } else if (sortDirection === 'asc') {
        // Reset to original order
        setSortColumn(null);
        setSortDirection(null);
        setOrders([...originalOrders]);
      }
    } else {
      // New column - start with descending
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  // Handle print full order - prints all selected orders
  const handlePrintOrder = async (e) => {
    e.stopPropagation();

    if (selectedOrders.length === 0) {
      console.log('No orders selected');
      return;
    }

    const selectedOrderObjects = orders.filter(order => selectedOrders.includes(order.id));
    let successCount = 0;
    let failCount = 0;

    for (const order of selectedOrderObjects) {
      const payload = {
        printerConfig: {
          ip: settings.printerIp,
          port: parseInt(settings.printerPort),
          width: parseInt(settings.printerWidth)
        },
        orderData: {
          order_number: order.order_number,
          store: storeMap[order.store_id],
          customer_name: `${order.customer.firstName} ${order.customer.lastName}`,
          pickup_date: order.pickup_date,
          pickup_time: order.pickup_time,
          order_data: order.order_data.items.map(item => ({
            item_name: item.title,
            quantity: item.quantity
          }))
        }
      };

      try {
        const response = await fetch('http://localhost:1120/print/order', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload)
        });

        if (!response.ok) {
          throw new Error(`Print failed: ${response.status}`);
        }

        console.log(`Order #${order.order_number} printed successfully`);
        markOrderAsPrinted(order.order_number);
        successCount++;
      } catch (err) {
        console.error(`Print error for order #${order.order_number}:`, err);
        failCount++;
      }
    }

    console.log(`Order print completed: ${successCount}/${selectedOrderObjects.length} orders printed (${failCount} failed)`);
  };

  // Handle print order detail (prints all details from all selected orders)
  const handlePrintOrderDetail = async (e) => {
    e.stopPropagation();

    if (selectedOrders.length === 0) {
      console.log('No orders selected');
      return;
    }

    const selectedOrderObjects = orders.filter(order => selectedOrders.includes(order.id));
    let successCount = 0;
    let failCount = 0;
    let totalLabels = 0;
    const printedOrderNumbers = new Set();

    // Print all detail labels from all selected orders
    for (const order of selectedOrderObjects) {
      const items = order.order_data.items;

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        const quantity = item.quantity || 1;

        // Print one label for each quantity
        for (let q = 0; q < quantity; q++) {
          totalLabels++;

          const payload = {
            printerConfig: {
              ip: settings.printerIp,
              port: parseInt(settings.printerPort),
              width: parseInt(settings.printerWidth)
            },
            orderData: {
              order_number: order.order_number,
              store: storeMap[order.store_id],
              customer_name: `${order.customer.firstName} ${order.customer.lastName}`,
              pickup_date: order.pickup_date,
              pickup_time: order.pickup_time,
              order_data: {
                item_name: item.title,
                quantity: 1  // Each label represents 1 physical item
              }
            }
          };

          try {
            const response = await fetch('http://localhost:1120/print/order-detail', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(payload)
            });

            if (!response.ok) {
              throw new Error(`Print failed: ${response.status}`);
            }

            console.log(`Detail label ${successCount + 1}: Order #${order.order_number} - ${item.title} (${q + 1}/${quantity})`);
            successCount++;
            printedOrderNumbers.add(order.order_number);
          } catch (err) {
            console.error(`Print error for Order #${order.order_number} - ${item.title} #${q + 1}:`, err);
            failCount++;
          }
        }
      }
    }

    // Log summary
    console.log(`Detail print completed: ${successCount}/${totalLabels} labels sent (${failCount} failed)`);

    // Mark all orders as printed if at least one label was printed successfully
    printedOrderNumbers.forEach(orderNumber => {
      markOrderAsPrinted(orderNumber);
    });
  };

  // Handle print single order detail (only the currently viewed item)
  const handlePrintSingleDetail = async (e) => {
    e.stopPropagation();

    const currentItem = getCurrentDetailItem();
    if (!currentItem) {
      console.log('No item selected');
      return;
    }

    const quantity = currentItem.quantity || 1;
    let successCount = 0;
    let failCount = 0;

    // Print one label for each quantity of the current item
    for (let q = 0; q < quantity; q++) {
      const payload = {
        printerConfig: {
          ip: settings.printerIp,
          port: parseInt(settings.printerPort),
          width: parseInt(settings.printerWidth)
        },
        orderData: {
          order_number: currentItem.orderNumber,
          store: storeMap[currentItem.storeId],
          customer_name: `${currentItem.customer.firstName} ${currentItem.customer.lastName}`,
          pickup_date: currentItem.pickupDate,
          pickup_time: currentItem.pickupTime,
          order_data: {
            item_name: currentItem.title,
            quantity: 1  // Each label represents 1 physical item
          }
        }
      };

      try {
        const response = await fetch('http://localhost:1120/print/order-detail', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload)
        });

        if (!response.ok) {
          throw new Error(`Print failed: ${response.status}`);
        }

        console.log(`Print job ${successCount + 1}: ${currentItem.title} (${q + 1}/${quantity})`);
        successCount++;
      } catch (err) {
        console.error(`Print error for ${currentItem.title} #${q + 1}:`, err);
        failCount++;
      }
    }

    console.log(`Print completed: ${successCount}/${quantity} labels sent for ${currentItem.title} (${failCount} failed)`);

    // Mark order as printed if at least one label was printed successfully
    if (successCount > 0) {
      markOrderAsPrinted(currentItem.orderNumber);
    }
  };

  // Handle print all (All Order labels + All Detail labels from all selected orders)
  const handlePrintAll = async (e) => {
    e.stopPropagation();

    if (selectedOrders.length === 0) {
      console.log('No orders selected');
      return;
    }

    const selectedOrderObjects = orders.filter(order => selectedOrders.includes(order.id));
    let orderSuccessCount = 0;
    let orderFailCount = 0;
    let detailsSuccessCount = 0;
    let detailsFailCount = 0;
    let totalDetailLabels = 0;
    const printedOrderNumbers = new Set();

    // First, print all order labels
    for (const order of selectedOrderObjects) {
      try {
        const orderPayload = {
          printerConfig: {
            ip: settings.printerIp,
            port: parseInt(settings.printerPort),
            width: parseInt(settings.printerWidth)
          },
          orderData: {
            order_number: order.order_number,
            store: storeMap[order.store_id],
            customer_name: `${order.customer.firstName} ${order.customer.lastName}`,
            pickup_date: order.pickup_date,
            pickup_time: order.pickup_time,
            order_data: order.order_data.items.map(item => ({
              item_name: item.title,
              quantity: item.quantity
            }))
          }
        };

        const orderResponse = await fetch('http://localhost:1120/print/order', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(orderPayload)
        });

        if (orderResponse.ok) {
          console.log(`Order #${order.order_number} label printed successfully`);
          orderSuccessCount++;
          printedOrderNumbers.add(order.order_number);
        } else {
          console.error(`Order #${order.order_number} label print failed:`, orderResponse.status);
          orderFailCount++;
        }
      } catch (err) {
        console.error(`Order #${order.order_number} label print error:`, err);
        orderFailCount++;
      }
    }

    // Then, print all detail labels from all selected orders
    for (const order of selectedOrderObjects) {
      const items = order.order_data.items;

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        const quantity = item.quantity || 1;

        for (let q = 0; q < quantity; q++) {
          totalDetailLabels++;

          const detailPayload = {
            printerConfig: {
              ip: settings.printerIp,
              port: parseInt(settings.printerPort),
              width: parseInt(settings.printerWidth)
            },
            orderData: {
              order_number: order.order_number,
              store: storeMap[order.store_id],
              customer_name: `${order.customer.firstName} ${order.customer.lastName}`,
              pickup_date: order.pickup_date,
              pickup_time: order.pickup_time,
              order_data: {
                item_name: item.title,
                quantity: 1
              }
            }
          };

          try {
            const detailResponse = await fetch('http://localhost:1120/print/order-detail', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(detailPayload)
            });

            if (detailResponse.ok) {
              console.log(`Detail label ${detailsSuccessCount + 1}: Order #${order.order_number} - ${item.title} (${q + 1}/${quantity})`);
              detailsSuccessCount++;
              printedOrderNumbers.add(order.order_number);
            } else {
              console.error(`Detail label print failed for Order #${order.order_number} - ${item.title} #${q + 1}:`, detailResponse.status);
              detailsFailCount++;
            }
          } catch (err) {
            console.error(`Detail label print error for Order #${order.order_number} - ${item.title} #${q + 1}:`, err);
            detailsFailCount++;
          }
        }
      }
    }

    // Log summary
    console.log(`Print All completed:`);
    console.log(`- Order labels: ${orderSuccessCount}/${selectedOrderObjects.length} printed (${orderFailCount} failed)`);
    console.log(`- Detail labels: ${detailsSuccessCount}/${totalDetailLabels} printed (${detailsFailCount} failed)`);

    // Mark all orders as printed if at least one label was printed successfully
    printedOrderNumbers.forEach(orderNumber => {
      markOrderAsPrinted(orderNumber);
    });
  };

  // Handle navigation between detail items (across all selected orders)
  const handlePreviousDetailItem = (e) => {
    e.stopPropagation();
    setCurrentDetailItemIndex(prev => Math.max(0, prev - 1));
  };

  const handleNextDetailItem = (e) => {
    e.stopPropagation();
    const allItems = getAllSelectedItems();
    if (allItems.length > 0) {
      setCurrentDetailItemIndex(prev => Math.min(allItems.length - 1, prev + 1));
    }
  };

  // Reset detail item index when selection changes
  useEffect(() => {
    setCurrentDetailItemIndex(0);
  }, [selectedOrders]);

  // Manual Order Modal handlers
  const handleManualOrderOpen = () => {
    setShowManualOrderModal(true);
  };

  const handleManualOrderClose = () => {
    setShowManualOrderModal(false);
    // Reset form
    setManualOrder({
      orderNumber: '',
      storeId: '1',
      customerFirstName: '',
      customerLastName: '',
      pickupDate: '',
      pickupTime: '',
      total: ''
    });
    setManualOrderItems([{ title: '', quantity: 1, unit_price: 0 }]);
  };

  const handleManualOrderChange = (e) => {
    const { name, value } = e.target;
    setManualOrder(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleManualOrderItemChange = (index, field, value) => {
    setManualOrderItems(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        [field]: field === 'quantity' ? parseInt(value) || 1 : field === 'unit_price' ? parseFloat(value) || 0 : value
      };
      return updated;
    });
  };

  const handleAddOrderItem = () => {
    setManualOrderItems(prev => [...prev, { title: '', quantity: 1, unit_price: 0 }]);
  };

  const handleRemoveOrderItem = (index) => {
    if (manualOrderItems.length > 1) {
      setManualOrderItems(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleManualOrderSubmit = async () => {
    // Build the payload in the expected database format
    const payload = {
      order_number: manualOrder.orderNumber,
      store_id: parseInt(manualOrder.storeId),
      pickup_date: manualOrder.pickupDate,
      pickup_time: manualOrder.pickupTime,
      total: parseFloat(manualOrder.total) || 0,
      created_at: new Date().toISOString(),
      order_data: JSON.stringify({
        items: manualOrderItems.map(item => ({
          item_id: null,
          title: item.title,
          category: null,
          quantity: item.quantity,
          unit_price: item.unit_price,
          tax_rate: null,
          item_subtotal: item.unit_price * item.quantity,
          item_tax: null,
          item_total: item.unit_price * item.quantity
        })),
        customer: {
          firstName: manualOrder.customerFirstName,
          lastName: manualOrder.customerLastName,
          email: null,
          phone: null
        },
        billing: null
      })
    };

    console.log('=== MANUAL ORDER PAYLOAD ===');
    console.log('Payload to be saved to database:', payload);
    console.log('order_data parsed:', JSON.parse(payload.order_data));
    console.log('============================');

    try {
      // Ensure server URL has protocol
      let serverUrl = settings.server;
      if (!serverUrl.startsWith('http://') && !serverUrl.startsWith('https://')) {
        serverUrl = 'https://' + serverUrl;
      }

      const apiUrl = `${serverUrl}/api/?request=manual`;
      console.log('Sending to:', apiUrl);

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        mode: 'cors',
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (result.status === 'success') {
        console.log('Manual order created successfully:', result);
        // Close modal and refresh orders
        handleManualOrderClose();
        fetchOrders();
      } else {
        throw new Error(result.message || 'Failed to create manual order');
      }
    } catch (err) {
      console.error('Error creating manual order:', err);
      alert('Error creating manual order: ' + err.message);
    }
  };

  // Date Events Handlers
  const fetchDateRanges = async () => {
    if (!settings.server) return;

    try {
      let serverUrl = settings.server;
      if (!serverUrl.startsWith('http://') && !serverUrl.startsWith('https://')) {
        serverUrl = 'https://' + serverUrl;
      }

      const apiUrl = `${serverUrl}/api/?request=getDates`;
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        mode: 'cors',
      });

      const result = await response.json();
      if (result.status === 'success') {
        setDateRanges(result.data || []);
      }
    } catch (err) {
      console.error('Error fetching date ranges:', err);
    }
  };

  const handleDateFilterChange = (value) => {
    if (value === 'edit-events') {
      setShowDateEventsModal(true);
      fetchDateRanges();
    } else {
      setSelectedDate(value);
    }
  };

  const handleDateEventsClose = () => {
    fetchDateRanges();
    setShowDateEventsModal(false);
    setSelectedDate('All');
  };

  const handleAddNewDateRange = () => {
    setEditingDateRange(null);
    setDateRangeForm({ name: '', start: '', end: '' });
    setShowDateEditModal(true);
  };

  const handleEditDateRange = (dateRange) => {
    setEditingDateRange(dateRange);
    setDateRangeForm({
      name: dateRange.name || '',
      start: dateRange.start,
      end: dateRange.end
    });
    setShowDateEditModal(true);
  };

  const handleDateEditClose = () => {
    setShowDateEditModal(false);
    setEditingDateRange(null);
    setDateRangeForm({ name: '', start: '', end: '' });
  };

  const handleDateRangeFormChange = (e) => {
    const { name, value } = e.target;
    setDateRangeForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveDateRange = async () => {
    if (!dateRangeForm.name || !dateRangeForm.start || !dateRangeForm.end) {
      alert('Please fill in all fields');
      return;
    }

    try {
      let serverUrl = settings.server;
      if (!serverUrl.startsWith('http://') && !serverUrl.startsWith('https://')) {
        serverUrl = 'https://' + serverUrl;
      }

      const isEditing = editingDateRange !== null;
      const apiUrl = isEditing
        ? `${serverUrl}/api/?request=updateDate`
        : `${serverUrl}/api/?request=createDate`;

      const payload = {
        name: dateRangeForm.name,
        start: dateRangeForm.start,
        end: dateRangeForm.end
      };

      if (isEditing) {
        payload.id = editingDateRange.id;
      }

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        mode: 'cors',
        body: JSON.stringify(payload)
      });

      const result = await response.json();
      if (result.status === 'success') {
        handleDateEditClose();
        fetchDateRanges();
      } else {
        throw new Error(result.message || 'Failed to save date range');
      }
    } catch (err) {
      console.error('Error saving date range:', err);
      alert('Error saving date range: ' + err.message);
    }
  };

  const handleDeleteDateRange = async (dateRange) => {
    if (!window.confirm(`Are you sure you want to delete "${dateRange.name}"?`)) {
      return;
    }

    try {
      let serverUrl = settings.server;
      if (!serverUrl.startsWith('http://') && !serverUrl.startsWith('https://')) {
        serverUrl = 'https://' + serverUrl;
      }

      const apiUrl = `${serverUrl}/api/?request=deleteDate`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        mode: 'cors',
        body: JSON.stringify({ id: dateRange.id })
      });

      const result = await response.json();
      if (result.status === 'success') {
        fetchDateRanges();
      } else {
        throw new Error(result.message || 'Failed to delete date range');
      }
    } catch (err) {
      console.error('Error deleting date range:', err);
      alert('Error deleting date range: ' + err.message);
    }
  };

  // Show loading while checking server
  if (serverConfigured === null) {
    return (
      <div className="server-check-container">
        <div className="server-check-box">
          <h1>Checking Print Server...</h1>
          <div className="loading-spinner"></div>
          <p>Verifying print server configuration</p>
        </div>
      </div>
    );
  }

  // Show error if server not configured
  if (serverConfigured === false) {
    return (
      <div className="server-error-container">
        <div className="server-error-box">
          <h1>Print Server Not Configured</h1>
        </div>
      </div>
    );
  }

  // Main application (server is configured)
  return (
    <div className="App">
      <div className="top-bar">
        <div className="filter-groups">
          <div className="filter-group">
            <label className="filter-label">Dates:</label>
            <select
              className="filter-select"
              value={selectedDate}
              onChange={(e) => handleDateFilterChange(e.target.value)}
            >
              <option value="All">All</option>
              {dateRanges.map((range) => (
                <option key={range.id} value={range.id}>
                  {range.name}
                </option>
              ))}
              <option value="edit-events">Edit Events</option>
            </select>
          </div>
          <div className="filter-group">
            <label className="filter-label">Location:</label>
            <select
              className="filter-select"
              value={selectedStore}
              onChange={(e) => handleStoreFilter(e.target.value)}
            >
              <option value="All">All</option>
              <option value="Palisades Park">Palisades Park</option>
              <option value="Princeton">Princeton</option>
              <option value="Fort Lee">Fort Lee</option>
            </select>
          </div>
        </div>
        <div className="action-buttons">
          <button className="action-btn" onClick={handleManualOrderOpen}>
            <Icon path={mdiPlus} size={0.8} />
            Manual Order
          </button>
          <button className="action-btn" onClick={handleRefresh}>
            <Icon path={mdiRefresh} size={0.8} />
            Refresh
          </button>
          <button className="action-btn" onClick={handleSettings}>
            <Icon path={mdiCog} size={0.8} />
            Settings
          </button>
        </div>
      </div>
      <div className="main-content">
        <div className="left-section">
          <div className="table-container">
            <table className="orders-table">
              <thead>
                <tr>
                  <th className="checkbox-column">
                    <input
                      type="checkbox"
                      checked={selectAll}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th className="sortable-header" onClick={() => handleSort('order_number')}>
                    Order #
                    {sortColumn === 'order_number' && (
                      <Icon
                        path={sortDirection === 'asc' ? mdiSortAscending : mdiSortDescending}
                        size={0.7}
                        className="sort-icon"
                      />
                    )}
                  </th>
                  <th className="sortable-header" onClick={() => handleSort('store')}>
                    Store
                    {sortColumn === 'store' && (
                      <Icon
                        path={sortDirection === 'asc' ? mdiSortAscending : mdiSortDescending}
                        size={0.7}
                        className="sort-icon"
                      />
                    )}
                  </th>
                  <th className="sortable-header" onClick={() => handleSort('customer')}>
                    Customer
                    {sortColumn === 'customer' && (
                      <Icon
                        path={sortDirection === 'asc' ? mdiSortAscending : mdiSortDescending}
                        size={0.7}
                        className="sort-icon"
                      />
                    )}
                  </th>
                  <th>Order</th>
                  <th className="sortable-header" onClick={() => handleSort('total')}>
                    Total
                    {sortColumn === 'total' && (
                      <Icon
                        path={sortDirection === 'asc' ? mdiSortAscending : mdiSortDescending}
                        size={0.7}
                        className="sort-icon"
                      />
                    )}
                  </th>
                  <th className="sortable-header" onClick={() => handleSort('pickup_date')}>
                    Pickup Date
                    {sortColumn === 'pickup_date' && (
                      <Icon
                        path={sortDirection === 'asc' ? mdiSortAscending : mdiSortDescending}
                        size={0.7}
                        className="sort-icon"
                      />
                    )}
                  </th>
                  <th className="sortable-header" onClick={() => handleSort('pickup_time')}>
                    Pickup Time
                    {sortColumn === 'pickup_time' && (
                      <Icon
                        path={sortDirection === 'asc' ? mdiSortAscending : mdiSortDescending}
                        size={0.7}
                        className="sort-icon"
                      />
                    )}
                  </th>
                  <th className="sortable-header" onClick={() => handleSort('status')}>
                    Status
                    {sortColumn === 'status' && (
                      <Icon
                        path={sortDirection === 'asc' ? mdiSortAscending : mdiSortDescending}
                        size={0.7}
                        className="sort-icon"
                      />
                    )}
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="9" style={{textAlign: 'center', padding: '20px'}}>
                      Loading orders...
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td colSpan="9" style={{textAlign: 'center', padding: '20px', color: 'red'}}>
                      Error: {error}
                    </td>
                  </tr>
                ) : getFilteredOrders().length === 0 ? (
                  <tr>
                    <td colSpan="9" style={{textAlign: 'center', padding: '20px'}}>
                      No orders to display
                    </td>
                  </tr>
                ) : (
                  getFilteredOrders().map((order) => (
                    <tr
                      key={order.id}
                      onClick={(e) => handleRowClick(order.id, e)}
                      className={selectedOrders.includes(order.id) ? 'selected-row' : ''}
                    >
                      <td className="checkbox-column">
                        <input
                          type="checkbox"
                          checked={selectedOrders.includes(order.id)}
                          onChange={() => handleOrderSelect(order.id)}
                        />
                      </td>
                      <td>{order.order_number}</td>
                      <td>{storeMap[order.store_id]}</td>
                      <td>{order.customer.firstName} {order.customer.lastName}</td>
                      <td>
                        {order.order_data.items.map((item, index) => (
                          <div key={index}>
                            {item.title} (x{item.quantity})
                          </div>
                        ))}
                      </td>
                      <td>${order.total.toFixed(2)}</td>
                      <td>{order.pickup_date}</td>
                      <td>{order.pickup_time}</td>
                      <td>
                        {printedOrders.has(order.order_number) && (
                          <span className="status-badge printed">Printed</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div className="right-section">
          <div className="controls-card">
            <div className="template-controls">
              <div className="template-groups-row">
                {/* Order Section */}
                <div className="template-group">
                  <button
                    className={`template-label-btn ${selectedTemplate === 'order' ? 'active' : ''}`}
                    onClick={() => setSelectedTemplate('order')}
                  >
                    Order
                  </button>
                  <button
                    className="template-action-btn print-btn"
                    onClick={handlePrintOrder}
                    disabled={selectedOrders.length === 0 || selectedTemplate !== 'order'}
                  >
                    <Icon path={mdiPrinterOutline} size={0.8} />
                    Print
                  </button>
                </div>

                {/* Details Section */}
                <div className="template-group">
                  <button
                    className={`template-label-btn ${selectedTemplate === 'order_details' ? 'active' : ''}`}
                    onClick={() => setSelectedTemplate('order_details')}
                  >
                    Details
                  </button>
                  <div className="details-controls">
                    <button
                      className="template-action-btn nav-btn"
                      onClick={handlePreviousDetailItem}
                      disabled={selectedTemplate !== 'order_details' || selectedOrders.length === 0 || currentDetailItemIndex === 0 || getAllSelectedItems().length <= 1}
                    >
                      <Icon path={mdiArrowLeftThick} size={0.7} />
                    </button>
                    <button
                      className="template-action-btn nav-btn"
                      onClick={handleNextDetailItem}
                      disabled={selectedTemplate !== 'order_details' || selectedOrders.length === 0 || currentDetailItemIndex >= getAllSelectedItems().length - 1 || getAllSelectedItems().length <= 1}
                    >
                      <Icon path={mdiArrowRightThick} size={0.7} />
                    </button>
                    <button
                      className="template-action-btn print-btn"
                      onClick={handlePrintSingleDetail}
                      disabled={selectedTemplate !== 'order_details' || selectedOrders.length === 0}
                    >
                      <Icon path={mdiPrinterOutline} size={0.8} />
                      Print
                    </button>
                  </div>
                </div>
              </div>

              {/* Print All Section - Below both Order and Details */}
              <div className="print-all-section">
                <button
                  className="print-all-btn"
                  onClick={handlePrintAll}
                  disabled={selectedOrders.length === 0}
                >
                  <Icon path={mdiPrinterOutline} size={0.9} />
                  Print All
                </button>
              </div>
            </div>
          </div>
          <div className="display-card">
            <div className="template-display">
              {selectedOrders.length === 0 ? (
                <div className="no-selection-message">
                  Select an order to display Label
                </div>
              ) : (
                <>
                  {selectedTemplate === 'order' ? (
                    <OrderLabel
                      order={getDisplayOrder()}
                      storeName={getDisplayOrder() ? storeMap[getDisplayOrder().store_id] : ''}
                    />
                  ) : (
                    <OrderDetailsLabel
                      item={getCurrentDetailItem()}
                      storeName={getCurrentDetailItem() ? storeMap[getCurrentDetailItem().storeId] : ''}
                    />
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Settings Modal */}
      {showSettingsModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Settings</h2>
              <button className="modal-close" onClick={handleCloseModal}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="server">Server:</label>
                <input
                  type="text"
                  id="server"
                  name="server"
                  value={settings.server}
                  onChange={handleSettingChange}
                  placeholder="Enter server address"
                />
              </div>
              <div className="form-group">
                <label htmlFor="printerIp">Printer IP:</label>
                <input
                  type="text"
                  id="printerIp"
                  name="printerIp"
                  value={settings.printerIp}
                  onChange={handleSettingChange}
                  placeholder="Enter printer IP address"
                />
              </div>
              <div className="form-group">
                <label htmlFor="printerPort">Printer Port:</label>
                <input
                  type="text"
                  id="printerPort"
                  name="printerPort"
                  value={settings.printerPort}
                  onChange={handleSettingChange}
                  placeholder="Enter printer port"
                />
              </div>
              <div className="form-group">
                <label htmlFor="printerWidth">Printer Width:</label>
                <input
                  type="text"
                  id="printerWidth"
                  name="printerWidth"
                  value={settings.printerWidth}
                  onChange={handleSettingChange}
                  placeholder="Enter printer width (default: 576)"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={handleCloseModal}>Cancel</button>
              <button className="btn-save" onClick={handleSaveSettings}>Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Manual Order Modal */}
      {showManualOrderModal && (
        <div className="modal-overlay" onClick={handleManualOrderClose}>
          <div className="modal-content modal-wide" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Manual Order Entry</h2>
              <button className="modal-close" onClick={handleManualOrderClose}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group form-group-half">
                  <label htmlFor="orderNumber">Order #:</label>
                  <input
                    type="text"
                    id="orderNumber"
                    name="orderNumber"
                    value={manualOrder.orderNumber}
                    onChange={handleManualOrderChange}
                    placeholder="Enter order number"
                  />
                </div>
                <div className="form-group form-group-half">
                  <label htmlFor="storeId">Store:</label>
                  <select
                    id="storeId"
                    name="storeId"
                    value={manualOrder.storeId}
                    onChange={handleManualOrderChange}
                  >
                    <option value="1">Princeton</option>
                    <option value="2">Fort Lee</option>
                    <option value="3">Palisades Park</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group form-group-half">
                  <label htmlFor="customerFirstName">Customer First Name:</label>
                  <input
                    type="text"
                    id="customerFirstName"
                    name="customerFirstName"
                    value={manualOrder.customerFirstName}
                    onChange={handleManualOrderChange}
                    placeholder="First name"
                  />
                </div>
                <div className="form-group form-group-half">
                  <label htmlFor="customerLastName">Customer Last Name:</label>
                  <input
                    type="text"
                    id="customerLastName"
                    name="customerLastName"
                    value={manualOrder.customerLastName}
                    onChange={handleManualOrderChange}
                    placeholder="Last name"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group form-group-half">
                  <label htmlFor="pickupDate">Pickup Date:</label>
                  <input
                    type="date"
                    id="pickupDate"
                    name="pickupDate"
                    value={manualOrder.pickupDate}
                    onChange={handleManualOrderChange}
                  />
                </div>
                <div className="form-group form-group-half">
                  <label htmlFor="pickupTime">Pickup Time:</label>
                  <input
                    type="text"
                    id="pickupTime"
                    name="pickupTime"
                    value={manualOrder.pickupTime}
                    onChange={handleManualOrderChange}
                    placeholder="e.g., 2:00 PM"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="total">Total ($):</label>
                <input
                  type="number"
                  id="total"
                  name="total"
                  step="0.01"
                  value={manualOrder.total}
                  onChange={handleManualOrderChange}
                  placeholder="0.00"
                />
              </div>

              <div className="order-items-section">
                <div className="order-items-header">
                  <label>Order Items:</label>
                  <button type="button" className="btn-add-item" onClick={handleAddOrderItem}>
                    + Add Item
                  </button>
                </div>
                {manualOrderItems.map((item, index) => (
                  <div key={index} className="order-item-row">
                    <div className="order-item-fields">
                      <input
                        type="text"
                        value={item.title}
                        onChange={(e) => handleManualOrderItemChange(index, 'title', e.target.value)}
                        placeholder="Item name"
                        className="item-title-input"
                      />
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => handleManualOrderItemChange(index, 'quantity', e.target.value)}
                        placeholder="Qty"
                        min="1"
                        className="item-quantity-input"
                      />
                      <input
                        type="number"
                        step="0.01"
                        value={item.unit_price}
                        onChange={(e) => handleManualOrderItemChange(index, 'unit_price', e.target.value)}
                        placeholder="Price"
                        className="item-price-input"
                      />
                      <button
                        type="button"
                        className="btn-remove-item"
                        onClick={() => handleRemoveOrderItem(index)}
                        disabled={manualOrderItems.length === 1}
                      >
                        &times;
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={handleManualOrderClose}>Cancel</button>
              <button className="btn-save" onClick={handleManualOrderSubmit}>Submit</button>
            </div>
          </div>
        </div>
      )}

      {/* Date Events Modal */}
      {showDateEventsModal && (
        <div className="modal-overlay" onClick={handleDateEventsClose}>
          <div className="modal-content modal-wide" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Manage Date Events</h2>
              <button className="modal-close" onClick={handleDateEventsClose}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="date-events-header">
                <button className="btn-add-date" onClick={handleAddNewDateRange}>
                  <Icon path={mdiPlus} size={0.8} />
                  Add New Dates
                </button>
              </div>
              <div className="date-events-table-container">
                <table className="date-events-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Start Date</th>
                      <th>End Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dateRanges.length === 0 ? (
                      <tr>
                        <td colSpan="4" style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                          No date events found
                        </td>
                      </tr>
                    ) : (
                      dateRanges.map((dateRange) => (
                        <tr key={dateRange.id}>
                          <td>{dateRange.name}</td>
                          <td>{dateRange.start}</td>
                          <td>{dateRange.end}</td>
                          <td className="date-actions-cell">
                            <button
                              className="btn-edit-date"
                              onClick={() => handleEditDateRange(dateRange)}
                            >
                              Edit
                            </button>
                            <button
                              className="btn-delete-date"
                              onClick={() => handleDeleteDateRange(dateRange)}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={handleDateEventsClose}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Date Edit Modal */}
      {showDateEditModal && (
        <div className="modal-overlay" onClick={handleDateEditClose}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingDateRange ? 'Edit Date Event' : 'Add New Date Event'}</h2>
              <button className="modal-close" onClick={handleDateEditClose}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="dateName">Name:</label>
                <input
                  type="text"
                  id="dateName"
                  name="name"
                  value={dateRangeForm.name}
                  onChange={handleDateRangeFormChange}
                  placeholder="e.g., Thanksgiving 2025"
                />
              </div>
              <div className="form-row">
                <div className="form-group form-group-half">
                  <label htmlFor="dateStart">Start Date:</label>
                  <input
                    type="date"
                    id="dateStart"
                    name="start"
                    value={dateRangeForm.start}
                    onChange={handleDateRangeFormChange}
                  />
                </div>
                <div className="form-group form-group-half">
                  <label htmlFor="dateEnd">End Date:</label>
                  <input
                    type="date"
                    id="dateEnd"
                    name="end"
                    value={dateRangeForm.end}
                    onChange={handleDateRangeFormChange}
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={handleDateEditClose}>Cancel</button>
              <button className="btn-save" onClick={handleSaveDateRange}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
